import { TrackingError } from '@/domain/errors/tracking-error'
import { UserEnrollmentContentInfo } from '@/domain/usecases/content/enrollment/user-enrollment-content-info'
import errorMonitor from '@/infra/tracking/error-monitor'
import { AccountStore } from './account-store'
import { Account } from '@/domain/usecases/account/account-profile'
import { AnalyticsEventName } from './analytics-event-names'

/**
 * Push data to the data layer for analytics
 * @param data - Data to push
 */
export const track = (data: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(data)

  const isDebugEnabled = localStorage?.getItem('debugDataLayer') === 'true'

  if (isDebugEnabled) {
    console.groupCollapsed(`%c[Analytics] ${data.event}`, 'color: #4CAF50; font-weight: bold')
    console.log(data)
    console.groupEnd()
  }
}

/**
 * Helper to wrap tracking functions with error handling
 * Prevents tracking errors from breaking the user experience
 */
const safeTrack = <T extends (...args: never[]) => void>(fn: T): T => {
  return ((...args: Parameters<T>) => {
    try {
      fn(...args)
    } catch (error) {
      errorMonitor.captureException(new TrackingError(error))
    }
  }) as T
}

/**
 * Main tracking object for analytics events
 * Auto-injects generic data (user, session, navigation, timestamp) into all events
 * Works anywhere: React components, services, factories, utility functions
 */
export const AnalyticsTracker = {
  /**
   * Track a custom event
   *
   * @param eventName - Name of the event (use AnalyticsEventName constants)
   * @param eventData - Event-specific data (optional)
   * @param userEnrollmentContentInfo - User enrollment content info (optional, for content-related events)
   *
   * @example
   * AnalyticsTracker.trackEvent(AnalyticsEventName.BUTTON_CLICK, {
   *   button_name: 'subscribe',
   *   content_id: 123
   * })
   */
  trackEvent: safeTrack(function (
    eventName: string,
    eventData?: Record<string, unknown>,
    userEnrollmentContentInfo?: UserEnrollmentContentInfo.Model | null
  ) {
    const genericData = AccountStore.buildGenericTrackingData(userEnrollmentContentInfo)

    track({
      event: eventName,
      _clear: true,
      contentData: {
        skillVersion: '1.0.0',
        ...genericData,
        ...eventData
      }
    })
  }),

  /**
   * Track page view
   *
   * @param pagePath - Path of the page (optional, uses window.location.pathname if not provided)
   * @param pageTitle - Title of the page (optional)
   * @param userEnrollmentContentInfo - User enrollment content info (optional, for content-related pages)
   *
   * @example
   * // Uses current location
   * AnalyticsTracker.pageView()
   *
   * // Or specify custom path
   * AnalyticsTracker.pageView('/search', 'Search Content')
   */
  pageView: safeTrack(function (
    pagePath?: string,
    pageTitle?: string,
    userEnrollmentContentInfo?: UserEnrollmentContentInfo.Model | null
  ) {
    const eventData = {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title
    }

    AnalyticsTracker.trackEvent(AnalyticsEventName.PAGE_VIEW, eventData, userEnrollmentContentInfo)
  }),

  /**
   * Track user identification event
   * This is called when a user logs in or when the account is loaded
   */
  identifyUser: safeTrack(function (accountData: Account.Model) {
    const eventData = {
      ...AccountStore.buildUserData(accountData)
    }

    AnalyticsTracker.trackEvent(AnalyticsEventName.USER_IDENTIFY, eventData)
  })
}
